// material
import { useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, Card, CardMedia, CardContent, IconButton } from "@mui/material";
import MContainer from "components/MContainer";
import Slider from "react-slick";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import '../styles/components/customSlider.scss';

// ----------------------------------------------------------------------
const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(5),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '0',
    width: '60px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
  }
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  margin: theme.spacing(1),
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}));

const CarouselArrow = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 5,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    backgroundColor: 'white',
  },
}));

const CustomSlider = styled(Slider)(({ theme }) => ({
  '& .slick-track': {
    display: 'flex',
    padding: '20px 0',
    '& .slick-slide': {
      height: 'inherit',
      '& > div': {
        height: '100%',
      }
    }
  },
  '& .slick-dots': {
    bottom: '-5px',
    '& li button:before': {
      fontSize: '10px',
    },
    '& li.slick-active button:before': {
      color: theme.palette.primary.main,
    }
  }
}));

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <CarouselArrow 
      onClick={onClick} 
      aria-label="next slide"
      sx={{ right: { xs: '-12px', sm: '-18px' } }}
    >
      <ArrowForwardIosIcon fontSize="small" />
    </CarouselArrow>
  );
};

const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <CarouselArrow 
      onClick={onClick} 
      aria-label="previous slide"
      sx={{ left: { xs: '-12px', sm: '-18px' } }}
    >
      <ArrowBackIosNewIcon fontSize="small" />
    </CarouselArrow>
  );
};

const sliderSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  initialSlide: 0,
  arrows: true,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
      }
    },
    {
      breakpoint: 900,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      }
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '30px',
      }
    }
  ]
};

// ----------------------------------------------------------------------

export default function WhoWeServe(props) {
  const Items = props.products;
  const slideRef = useRef();

  return (
    <MContainer 
      id="whoWeServe" 
      className="paddedSection" 
      sx={{ 
        position: "relative", 
        pt: 7, 
        pb: 8,
        backgroundColor: 'background.paper'
      }}
    >
      <Box sx={{ mb: 6, pl: { xs: 2, md: 4 } }}>
        <SectionTitle variant="h2" color="text.primary">
          Who We Serve
        </SectionTitle>
      </Box>
      
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <CustomSlider {...sliderSettings} ref={slideRef}>
          {Items.map((product, index) => (
            <Box key={index} sx={{ height: '100%' }}>
              <ServiceCard>
                <CardMedia
                  component="img"
                  height="180"
                  image={product.image}
                  alt={product.handle}
                  sx={{ 
                    objectFit: 'cover',
                    objectPosition: 'center',
                    backgroundColor: 'rgba(0,0,0,0.05)'
                  }}
                />
                <CardContent sx={{ p: 3, height: 'calc(100% - 180px)' }}>
                  <Typography variant="h5" color="primary.main" sx={{ mb: 2, fontWeight: 600 }}>
                    {product.handle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {product.description}
                  </Typography>
                </CardContent>
              </ServiceCard>
            </Box>
          ))}
        </CustomSlider>
      </Box>
    </MContainer>
  );
}
